import {Injectable} from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {JsonApiResponse} from '../../shared/models/JsonApiResponse';
import {catchError, map} from 'rxjs/operators';
import {HttpErrorHandler} from '../../http.error.handler';
import {GeobufResponseHandler} from '../../geobuf.response.handler';
import {LocationSearchParams} from '../../shared/models/breadcrumb.model';
import {FeatureCollection} from 'geojson';
import {SessionMetricEventModel} from '../../shared/models/session-metric.model';
import {firstValueFrom, of} from 'rxjs';
import {SessionStatistics} from '../../shared/models/shift.model';
import { RouteTrackingStatus } from '../../shared/models/route-assignment';
import { TrackViewState } from '../../shared/models/location.model';

@Injectable({
  providedIn: 'root'
})
export class LocationApiService {

  constructor(
    private http: HttpClient,
  ) {
  }

  getTrackViewState() {
    return firstValueFrom(
      this.http.get<TrackViewState>(
        `${environment.services.location}v1/track/viewstate`,
        {
          headers: {Accept: 'application/json'}
        }
      )
        .pipe(
          catchError(HttpErrorHandler.handleError) // then handle the error
        )
    );
  }

  getCurrentLocationPoints() {
    return firstValueFrom(
      this.http.get(
        `${environment.services.location}v1/location/current`,
        {
          observe: 'response',
          responseType: 'arraybuffer',
          headers: {Accept: 'application/octet-stream'}
        }
      )
        .pipe(
          map(response => GeobufResponseHandler.handleResponse(response) as FeatureCollection),
          catchError(HttpErrorHandler.handleError) // then handle the error
        )
    );
  }

  getCurrentLocationPointsGeoJson() {
    return firstValueFrom(
      this.http.get<FeatureCollection>(
        `${environment.services.location}v1/location/current`,
        {
          headers: {Accept: 'application/json'}
        }
      )
        .pipe(
          catchError(HttpErrorHandler.handleError) // then handle the error
        )
    );
  }

  getLocationHistoryTrackHeads(snapshot: number) {
    return firstValueFrom(
      this.http.get(
        `${environment.services.location}v1/track/head?snapshot=${snapshot}`,
        {
          observe: 'response',
          responseType: 'arraybuffer',
          headers: {Accept: 'application/octet-stream'}
        }
      )
        .pipe(
          map(response => GeobufResponseHandler.handleResponse(response)),
          catchError(HttpErrorHandler.handleError) // then handle the error
        )
    );
  }

  search(searchParams: LocationSearchParams) {
    return firstValueFrom(
      this.http.post<FeatureCollection>(
        `${environment.services.location}v1/location/history/search`,
        searchParams,
        {
          headers: {Accept: 'application/json'}
        }
      ).pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      )
    );
  }

  searchGeobuf(params: LocationSearchParams) {
    return firstValueFrom(
      this.http.post(
        `${environment.services.location}v1/location/history/search`,
        params,
        {
          observe: 'response',
          responseType: 'arraybuffer',
          headers: {Accept: 'application/octet-stream'}
        }
      ).pipe(
        map(response => GeobufResponseHandler.handleResponse(response) as FeatureCollection),
        catchError(HttpErrorHandler.handleError)
      )
    );
  }

  closestSessionMetricEvent(
    shiftId: number,
    vehicleId: number,
    metricId: number,
    unixtime: number = Math.round(Date.now().valueOf() / 1000),
  ) {
    const request: SessionMetricEventModel = {
      locationSourceId: vehicleId,
      sessionKey: shiftId,
      metricId,
      unixtime
    };
    return firstValueFrom(
      this.http.post<JsonApiResponse<SessionMetricEventModel>>(
        `${environment.services.location}v1/session-metric/event/closest`,
        request,
        {
          headers: {Accept: 'application/json'}
        }
      )
        .pipe(
          catchError((err, caught) => {
            if (err.status === 500 && err.error && err.error.error && err.error.error.indexOf('404 NOT_FOUND') >= 0) {
              const other = new JsonApiResponse<SessionMetricEventModel>();
              other.data = new SessionMetricEventModel();
              return of(other);
            } else {
              return HttpErrorHandler.handleError(err);
            }
          })
        )
    );
  }

  getLocationHistoryVectorTileUrl(snapshot: number): string {
    return `${environment.services.location}v1/track/tile/{z}/{x}/{y}?snapshot=${snapshot}`;
  }

  getRoadSegments(flagMask: number = null, cutOff: Date = null) {
    let params: HttpParams = new HttpParams();
    if (!!flagMask) {
      params = params.set('flagMask', flagMask);
    }
    if (!!cutOff) {
      params = params.set('cutOff', Math.round(new Date(cutOff).valueOf() / 1000));
    }

    return firstValueFrom(
      this.http.get(
        `${environment.services.location}v1/road/segment`,
        {
          params,
          observe: 'response',
          responseType: 'arraybuffer',
          headers: {Accept: 'application/octet-stream'}
        }
      )
        .pipe(
          map(response => GeobufResponseHandler.handleResponse(response)),
          catchError(HttpErrorHandler.handleError) // then handle the error
        )
    );
  }

  getShiftStatistics(shiftId: number, timeFrom: Date = null, timeTo: Date = null) {
    let params: HttpParams = new HttpParams()
      .set('sessionKey', shiftId);
    if (timeFrom) {
      params = params.set('timeFrom', Math.round(new Date(timeFrom).valueOf() / 1000));
      if (timeTo) {
        params = params.set('timeTo', Math.round(new Date(timeTo).valueOf() / 1000));
      }
    }
    return firstValueFrom(
      this.http.get<JsonApiResponse<SessionStatistics[]>>(
        `${environment.services.location}v1/session/statistic`,
        {
          params,
        }
      )
        .pipe(
          catchError(HttpErrorHandler.handleError)
        )
    );
  }

  getShiftPoints(shiftId: number, vehicleId: number, timeFrom: Date = null, timeTo: Date = null): Promise<FeatureCollection> {
    let params: HttpParams = new HttpParams()
      .set('sessionKey', shiftId)
      .set('locationSourceId', vehicleId);
    if (timeFrom) {
      params = params.set('anchor', 'window');
      params = params.set('anchorTime', Math.round(new Date(timeFrom).valueOf() / 1000));
      params = params.set('timeTo', Math.round(!timeTo ? new Date().valueOf() : new Date(timeTo).valueOf() / 1000));
      if (timeTo) {
      }
    } else {
      params = params.set('anchorTime', 0);
    }
    return firstValueFrom(
      this.http.get(
        `${environment.services.location}v1/track/vertex`,
        {
          params,
          observe: 'response',
          responseType: 'arraybuffer',
          headers: {Accept: 'application/octet-stream'}
        }
      )
        .pipe(
          map(response => GeobufResponseHandler.handleResponse(response)),
          catchError(HttpErrorHandler.handleError)
        )
    );
  }

  getTrackGeometries(shiftId: number, vehicleId: number, timeFrom: Date = null, timeTo: Date = null): Promise<FeatureCollection> {
    let params: HttpParams = new HttpParams();
    if (shiftId) {
      params = params.set('sessionKey', shiftId);
    }
    if (vehicleId) {
      params = params.set('locationSourceId', vehicleId);
    }
    if (timeFrom) {
      params = params.set('timeFrom', Math.round(new Date(timeFrom).valueOf() / 1000));
    }
    if (timeTo) {
      params = params.set('timeTo', Math.round(new Date(timeTo).valueOf() / 1000));
    }
    return firstValueFrom(
      this.http.get(
        `${environment.services.location}v1/track/segment`,
        {
          params,
          observe: 'response',
          responseType: 'arraybuffer',
          headers: {Accept: 'application/octet-stream'}
        }
      )
        .pipe(
          map(response => GeobufResponseHandler.handleResponse(response)),
          catchError(HttpErrorHandler.handleError) // then handle the error
        )
    );
  }

  getShiftBoundingBox(shiftId: number) {
    return firstValueFrom(
      this.http.get<number[]>(
        `${environment.services.location}v1/track/${shiftId}/boundingBox`,
      )
        .pipe(
          catchError(HttpErrorHandler.handleError)
        )
    );
  }

  // this returns a list of trackings, i.e. different shifts etc.
  getRouteAssignmentTracking(routeConfigurationId: number, routeId: number) {
    return firstValueFrom(
      this.http
        .get<RouteTrackingStatus[]>(
          `${environment.services.location}v1/route/${routeConfigurationId}/${routeId}/tracking`, {}
        )
        .pipe(
          catchError(HttpErrorHandler.handleError)
        )
    );
  }

  getRouteSegmentGeoJson(routeConfigurationId: number, routeIds?: number[]) {
    let params: HttpParams = new HttpParams();
    if (routeIds && routeIds.length > 0) {
      params = params.set('routeId', routeIds.join(','));
    }
    return firstValueFrom(
      this.http.get(
        `${environment.services.location}v1/route/${routeConfigurationId}/segment/status`,
        {
          params,
          observe: 'response',
          responseType: 'arraybuffer',
          headers: {Accept: 'application/octet-stream'}
        }
      ).pipe(
        map(response => GeobufResponseHandler.handleResponse(response) as FeatureCollection),
        catchError(HttpErrorHandler.handleError)
      )
    );
  }
}
